import { Injectable } from '@angular/core';
import { ConnectionStatus } from '@capacitor/network';
import { BASE_API_URL, IAmenity, IBoondock, IBoondockWhere, IPhoto } from '@curbnturf/entities';
import { objectToUrlParams } from '@curbnturf/helpers';
import {
  CachedHttpClient,
  Logger,
  OfflineBoondockDbSet,
  OfflineBoondockPhotoEntity,
  OfflineBoondockPhotosDbSet,
} from '@curbnturf/network';
import { PhotoService } from '@curbnturf/photo';
import { StatusFacade } from '@curbnturf/status';
import { ToastController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, takeWhile } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

const API_URL = BASE_API_URL + 'listing';

@Injectable({
  providedIn: 'root',
})
export class OfflineBoondockService {
  // For this service, start with the assumption that we are online.
  connectionStatus: ConnectionStatus = { connected: true, connectionType: 'cellular' };

  get isOnline() {
    return this.connectionStatus.connected;
  }

  constructor(
    private http: CachedHttpClient,
    private logger: Logger,
    private offlineBoondockDbSet: OfflineBoondockDbSet,
    private offlineBoondockPhotosDbSet: OfflineBoondockPhotosDbSet,
    private photoService: PhotoService,
    private statusFacade: StatusFacade,
    private store: Store,
    private toastController: ToastController,
  ) {
    this.statusFacade.networkStatus$.subscribe((status) => {
      this.connectionStatus = status;
    });
  }

  create(boondock: IBoondock): Observable<IBoondock> {
    const storableBoondock = { ...boondock };
    if (!boondock.syncId) {
      storableBoondock.syncId = uuidv4();
    }
    // @todo check for photos to update and store those for later upload
    return from(this.offlineBoondockDbSet.store(storableBoondock)).pipe(map(() => storableBoondock));
  }

  createAmenity(boondock: IBoondock): Observable<IBoondock> {
    const amenities: IAmenity[] = [];
    if (boondock.amenities) {
      amenities.push(...boondock.amenities);
    }

    amenities.push({ boondock });

    return this.update({ ...boondock, amenities });
  }

  fetch(syncId: string): Observable<IBoondock> {
    return from(this.offlineBoondockDbSet.retrieve(syncId));
  }

  fetchAll(query: IBoondockWhere): Observable<IBoondock[]> {
    const queryString = objectToUrlParams(query);
    return this.http.get<IBoondock[]>(`${API_URL}?${queryString}`);
  }

  remove(syncId: string): Observable<IBoondock> {
    return from(this.offlineBoondockDbSet.retrieve(syncId)).pipe(
      mergeMap((boondockToRemove: IBoondock) => {
        // remove photos
        if (boondockToRemove.photos && boondockToRemove.photos.length > 0) {
          return forkJoin(
            boondockToRemove.photos?.map((photo) => {
              return from(this.offlineBoondockPhotosDbSet.removeByKey(photo.key)).pipe(
                map(() => {
                  return from(this.photoService.removeOfflinePhoto(photo.key)).pipe(
                    map(() => {
                      return boondockToRemove;
                    }),
                  );
                }),
              );
            }),
          ).pipe(mergeMap(() => from(this.offlineBoondockDbSet.remove(syncId)).pipe(map(() => boondockToRemove))));
        } else {
          return from(this.offlineBoondockDbSet.remove(syncId)).pipe(map(() => boondockToRemove));
        }
      }),
    );
  }

  restore(boondockId: number): Observable<IBoondock> {
    return this.http.post<IBoondock>(`${API_URL}/${boondockId}/restore`, null);
  }

  removeAmenity(boondock: IBoondock, amenity: IAmenity): Observable<IBoondock> {
    if (!boondock.amenities) {
      return of(boondock);
    }

    const amenities = [...boondock.amenities];

    const index = amenities.indexOf(amenity);

    if (index || index === 0) {
      amenities.splice(index, 1);
    }

    return this.update({ ...boondock, amenities });
  }

  update(boondock: IBoondock): Observable<IBoondock> {
    // @todo check for photos to update and store those for later upload
    return from(this.offlineBoondockDbSet.store(boondock)).pipe(map(() => boondock));
  }

  enable(boondock: IBoondock): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${boondock.id}/enable`, null);
  }

  disable(boondock: IBoondock): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${boondock.id}/disable`, null);
  }

  // @todo rework for offline functionality
  hide(boondock: IBoondock): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${boondock.id}/hide`, null);
  }

  // @todo rework for offline functionality
  show(boondock: IBoondock): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${boondock.id}/show`, null);
  }

  // @todo rework for offline functionality
  removeImage(boondockId: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${boondockId}/remove-image/${photoIndex}`);
  }

  uploadImage(syncId: string, photoFormData: FormData): Observable<{ syncId: string; photos: IPhoto[] }> {
    const file = photoFormData.get('files') as File;
    const caption = photoFormData.get('captions') as string;
    const entity: OfflineBoondockPhotoEntity = {
      syncId: syncId,
      key: 'unknown',
      caption,
    };

    return from(this.photoService.saveOfflinePhoto(file)).pipe(
      takeWhile((filename) => filename !== false),
      mergeMap((filename) => {
        if (filename) {
          entity.key = filename;
          const photo: IPhoto = {
            key: filename,
            caption: entity.caption,
          };
          // Check that we haven't already saved this photo to the database.
          return from(this.offlineBoondockPhotosDbSet.retrieveByKey(entity.key)).pipe(
            mergeMap((result) => {
              if (result.length === 0) {
                return from(this.offlineBoondockPhotosDbSet.store(entity)).pipe(map(() => ({ photos: [photo], syncId })));
              }
              return of({ photos: [photo], syncId });
            }),
          );
        } else {
          throw new Error('Failed to save uploaded image.');
        }
      }),
    );
  }

  // @todo rework for offline functionality
  favorite(listing: IBoondock): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/favorite`, null);
  }

  // @todo rework for offline functionality
  unfavorite(listing: IBoondock): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${listing.id}/unfavorite`, null);
  }

  uploadOfflinePhoto(photo: IPhoto, boondockId: number, syncId: string): Observable<IPhoto | void> {
    this.logger.debug('Uploading Photo', { boondockId, syncId, photo });
    return from(this.photoService.retrieveOfflinePhoto(photo.key)).pipe(
      mergeMap((file) => {
        if (file) {
          this.logger.debug('Photo found, proceeding to upload.', { boondockId, syncId, photo });
          // Setup the file data to send to the server
          const formData = new FormData();
          formData.append('files', file);
          formData.append('captions', photo.caption || '');
          formData.append('listingId', boondockId.toString());
          formData.append('syncId', syncId);

          return this.http.post<{ id: number; photos: IPhoto[] }>(`${API_URL}/${boondockId}/upload-image`, formData).pipe(
            map((photoResult) => {
              this.logger.debug('Successfully uploaded image: ' + photo.key, {
                boondockId,
                result: JSON.stringify(photoResult),
              });
              return photoResult.photos.pop();
            }),
            catchError((error) => {
              this.logger.error('Failed to upload listing image.', { boondockId, photo, error });
              return of(error);
            }),
          );
        } else {
          this.logger.warning('Local photo not found.', { boondockId, syncId, photo });
        }
        return of();
      }),
    );
  }
}
